import React, { FC, useEffect, useRef } from 'react';
import { sdk as imageSDK } from '@wix/image-kit';
import {
  ImageHoverEffectOptions as HoverEffectOptions,
  ImageLoadingBehaviorOptions as LoadingBehaviorOptions,
  ImageResizeOptions as ResizeOptions,
  ThumbnailImage,
} from 'wix-ui-tpa';
import { ImageDto } from '@wix/bookings-uou-types';
import { ImageResizeOptions } from '../../../../../../types/types';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useWidgetViewModel } from '../../../hooks/useWidgetViewModel';
import { classes, st } from './ServiceImage.st.css';
import { DataHooks } from './consts';

export type ServiceImageProps = {
  image: ImageDto | null;
  imageTitle: string;
  width: number;
  height: number;
  setBoundingClientRect?: Function;
  onClick: () => void;
};

const mapImageResizeOptionsToResizeOptions = {
  [ImageResizeOptions.CROP]: ResizeOptions.cover,
  [ImageResizeOptions.FIT]: ResizeOptions.contain,
};

export const ServiceImage: FC<ServiceImageProps> = (props) => {
  const { image, imageTitle, height, setBoundingClientRect, onClick } = props;
  const { isSEO, serviceImageViewModel } = useWidgetViewModel();
  const { imageResize, aspectRatio, focalPoint, isRoundGrid } =
    serviceImageViewModel;
  const imageRef = useRef() as React.MutableRefObject<any>;
  const { t } = useTranslation();
  const width = isRoundGrid ? height : props.width;
  useEffect(() => {
    if (setBoundingClientRect) {
      setTimeout(() => {
        if (imageRef?.current?.getBoundingClientRect()) {
          setBoundingClientRect(imageRef.current.getBoundingClientRect());
        }
      }, 100);
    }
  }, [imageRef, width, height]);

  const getImageUrl = () => {
    if (image) {
      const imageDimensions = isSEO
        ? {
            width: isRoundGrid ? image?.height : image?.width,
            height: image?.height,
          }
        : { width, height };
      const targetWidth = imageDimensions.width || 0;
      const targetHeight = imageDimensions.height || 0;
      return imageResize === ImageResizeOptions.CROP
        ? imageSDK.getScaleToFillImageURL(
            image.relativeUri,
            image.width,
            image.height,
            targetWidth,
            targetHeight,
            { focalPoint },
          )
        : imageSDK.getScaleToFitImageURL(
            image.relativeUri,
            image.width,
            image.height,
            targetWidth,
            targetHeight,
          );
    }
  };

  return (
    <div
      data-hook={DataHooks.ROOT}
      className={st(classes.root, {
        shape: isRoundGrid ? 'round' : 'square',
      })}
      ref={setBoundingClientRect ? imageRef : undefined}
    >
      <div
        data-hook={DataHooks.WRAPPER}
        className={classes.wrapper}
        style={{
          height: isRoundGrid ? `calc(100% - 32px)` : undefined,
          width: isRoundGrid ? `calc(${100 / aspectRatio}% - 32px)` : undefined,
        }}
      >
        <div
          data-hook={DataHooks.CONTAINER}
          onClick={onClick}
          className={classes.clickable}
          role="presentation"
          tabIndex={-1}
        >
          {image ? (
            <ThumbnailImage
              key={`image-key-${height}-${width}-${imageResize}-${focalPoint.x}-${focalPoint.y}-${aspectRatio}`}
              alt={t(imageTitle)}
              fluid
              width={width}
              height={height}
              focalPoint={focalPoint}
              loadingBehavior={LoadingBehaviorOptions.none}
              aspectRatio={aspectRatio}
              hoverEffect={HoverEffectOptions.darken}
              resize={mapImageResizeOptionsToResizeOptions[imageResize]}
              data-hook={DataHooks.IMAGE}
              className={classes.image}
              src={getImageUrl()}
              sourceHeight={image.height}
              sourceWidth={image.width}
              shouldUseLQIP={true}
              isSEOBot={isSEO}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
